import clsx, { type ClassValue } from 'clsx';
import { Check, Copy, X } from 'lucide-react';
import {
	type ButtonHTMLAttributes,
	type InputHTMLAttributes,
	type ReactNode,
	type SelectHTMLAttributes,
	type TextareaHTMLAttributes,
	forwardRef,
	useEffect,
	useRef,
	useState,
} from 'react';

import { twMerge } from 'tailwind-merge';

import { PROTOCOL_LABELS } from '@/lib/format';

/** Class names with later Tailwind utilities overriding earlier conflicting ones. */
export const cx = (...c: ClassValue[]) => twMerge(clsx(c));

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';

const VARIANTS: Record<Variant, string> = {
	primary: 'bg-accent text-accent-fg hover:opacity-90 shadow-sm',
	secondary: 'bg-surface text-fg border border-border hover:bg-muted',
	ghost: 'text-fg-muted hover:bg-muted hover:text-fg',
	danger: 'bg-surface text-danger border border-border hover:bg-danger-soft',
};

export const Button = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: 'sm' | 'md' }>(
	function Button({ variant = 'secondary', size = 'md', className, type = 'button', ...rest }, ref) {
		return (
			<button
				ref={ref}
				type={type}
				className={cx(
					'inline-flex items-center justify-center gap-1.5 rounded-md font-medium transition disabled:opacity-50 disabled:pointer-events-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
					size === 'sm' ? 'h-7 px-2.5 text-xs' : 'h-9 px-3.5 text-sm',
					VARIANTS[variant],
					className,
				)}
				{...rest}
			/>
		);
	},
);

export function Card({ title, actions, children, className }: { title?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string }) {
	return (
		<section className={cx('rounded-lg border border-border bg-surface', className)}>
			{(title || actions) && (
				<header className="flex items-center justify-between gap-3 border-b border-border px-4 py-2.5">
					<h2 className="text-sm font-semibold">{title}</h2>
					<div className="flex items-center gap-2">{actions}</div>
				</header>
			)}
			<div className="p-4">{children}</div>
		</section>
	);
}

export function Badge({ children, tone = 'neutral', className }: { children: ReactNode; tone?: 'neutral' | 'accent' | 'ok' | 'warn' | 'danger'; className?: string }) {
	const tones = {
		neutral: 'bg-muted text-fg-muted',
		accent: 'bg-accent-soft text-accent',
		ok: 'bg-ok/15 text-ok',
		warn: 'bg-warn/15 text-warn',
		danger: 'bg-danger-soft text-danger',
	};
	return <span className={cx('inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium whitespace-nowrap', tones[tone], className)}>{children}</span>;
}

export const ProtocolBadge = ({ protocol, muted }: { protocol: string; muted?: boolean }) => (
	<Badge tone={muted ? 'neutral' : 'accent'}>{PROTOCOL_LABELS[protocol] ?? protocol}</Badge>
);

const STATUS_TONE: Record<string, string> = {
	running: 'bg-ok',
	starting: 'bg-warn animate-pulse',
	stopped: 'bg-fg-muted/40',
	crashed: 'bg-danger animate-pulse',
	invalid: 'bg-danger',
};

export function StatusDot({ status, label = true }: { status: string; label?: boolean }) {
	return (
		<span className="inline-flex items-center gap-1.5 text-xs text-fg-muted">
			<span className={cx('size-2 rounded-full', STATUS_TONE[status] ?? 'bg-fg-muted')} aria-hidden />
			{label && <span className="capitalize">{status}</span>}
		</span>
	);
}

const inputCls =
	'w-full rounded-md border border-border bg-surface px-2.5 py-1.5 text-sm placeholder:text-fg-muted/70 focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/25 disabled:opacity-60';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }>(function Input(
	{ className, invalid, ...rest },
	ref,
) {
	return <input ref={ref} className={cx(inputCls, invalid && 'border-danger', className)} {...rest} />;
});

export function Select({ className, invalid, ...rest }: SelectHTMLAttributes<HTMLSelectElement> & { invalid?: boolean }) {
	return <select className={cx(inputCls, 'pr-7', invalid && 'border-danger', className)} {...rest} />;
}

export function Textarea({ className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
	return <textarea className={cx(inputCls, 'font-mono text-xs', className)} {...rest} />;
}

export function Field({ label, hint, error, children, className }: { label: ReactNode; hint?: ReactNode; error?: string; children: ReactNode; className?: string }) {
	return (
		<label className={cx('flex flex-col gap-1', className)}>
			<span className="text-xs font-medium text-fg-muted">{label}</span>
			{children}
			{error ? <span className="text-xs text-danger">{error}</span> : hint ? <span className="text-[11px] text-fg-muted/80">{hint}</span> : null}
		</label>
	);
}

export function Switch({ checked, onChange, label, disabled }: { checked: boolean; onChange: (v: boolean) => void; label?: ReactNode; disabled?: boolean }) {
	return (
		<label className={cx('inline-flex shrink-0 cursor-pointer items-center gap-2 text-sm whitespace-nowrap select-none', disabled && 'opacity-50')}>
			<button
				type="button"
				role="switch"
				aria-checked={checked}
				disabled={disabled}
				onClick={() => onChange(!checked)}
				className={cx('relative h-5 w-9 shrink-0 rounded-full transition', checked ? 'bg-accent' : 'bg-border')}
			>
				<span className={cx('absolute top-0.5 size-4 rounded-full bg-white shadow transition', checked ? 'left-4.5' : 'left-0.5')} />
			</button>
			{label}
		</label>
	);
}

export function Tabs<T extends string>({ tabs, value, onChange }: { tabs: { id: T; label: ReactNode }[]; value: T; onChange: (t: T) => void }) {
	return (
		<div role="tablist" className="flex gap-1 overflow-x-auto border-b border-border">
			{tabs.map((t) => (
				<button
					key={t.id}
					role="tab"
					aria-selected={value === t.id}
					onClick={() => onChange(t.id)}
					className={cx(
						'-mb-px border-b-2 px-3 py-2 text-sm whitespace-nowrap transition',
						value === t.id ? 'border-accent font-medium text-fg' : 'border-transparent text-fg-muted hover:text-fg',
					)}
				>
					{t.label}
				</button>
			))}
		</div>
	);
}

export function Dialog({ open, onClose, title, children, wide }: { open: boolean; onClose: () => void; title: ReactNode; children: ReactNode; wide?: boolean }) {
	const ref = useRef<HTMLDialogElement>(null);
	useEffect(() => {
		const d = ref.current;
		if (!d) return;
		if (open && !d.open) d.showModal();
		if (!open && d.open) d.close();
	}, [open]);
	return (
		<dialog
			ref={ref}
			onClose={onClose}
			onClick={(e) => e.target === ref.current && onClose()}
			className={cx(
				'm-auto w-[calc(100%-2rem)] rounded-xl border border-border bg-surface p-0 text-fg shadow-2xl backdrop:bg-black/40',
				wide ? 'max-w-3xl' : 'max-w-lg',
			)}
		>
			{open && (
				<div className="flex max-h-[85vh] flex-col">
					<header className="flex items-center justify-between border-b border-border px-5 py-3">
						<h2 className="font-semibold">{title}</h2>
						<Button variant="ghost" size="sm" onClick={onClose} aria-label="Close">
							<X className="size-4" />
						</Button>
					</header>
					<div className="overflow-y-auto p-5">{children}</div>
				</div>
			)}
		</dialog>
	);
}

export function CopyButton({ text }: { text: string }) {
	const [done, setDone] = useState(false);
	return (
		<Button
			variant="ghost"
			size="sm"
			aria-label="Copy"
			onClick={async () => {
				await navigator.clipboard.writeText(text);
				setDone(true);
				setTimeout(() => setDone(false), 1200);
			}}
		>
			{done ? <Check className="size-3.5 text-ok" /> : <Copy className="size-3.5" />}
		</Button>
	);
}

export function CodeBlock({ code, label }: { code: string; label?: string }) {
	return (
		<div className="overflow-hidden rounded-md border border-border">
			<div className="flex items-center justify-between bg-muted px-3 py-1">
				<span className="text-xs text-fg-muted">{label}</span>
				<CopyButton text={code} />
			</div>
			<pre className="overflow-x-auto p-3 font-mono text-xs leading-relaxed">{code}</pre>
		</div>
	);
}

export function Empty({ title, children, action }: { title: ReactNode; children?: ReactNode; action?: ReactNode }) {
	return (
		<div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border px-6 py-12 text-center">
			<p className="font-medium">{title}</p>
			{children && <p className="max-w-md text-sm text-fg-muted">{children}</p>}
			{action && <div className="mt-2">{action}</div>}
		</div>
	);
}

export const Spinner = () => <span className="inline-block size-4 animate-spin rounded-full border-2 border-border border-t-accent" aria-label="Loading" />;

export function PageHeader({ title, subtitle, actions }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode }) {
	return (
		<div className="mb-5 flex flex-wrap items-end justify-between gap-3">
			<div className="min-w-0">
				<h1 className="truncate text-xl font-semibold tracking-tight">{title}</h1>
				{subtitle && <p className="mt-0.5 text-sm text-fg-muted">{subtitle}</p>}
			</div>
			{actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
		</div>
	);
}
